import React, { useState, useMemo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-toastify';
import { KTSVG } from '../../../../_metronic/helpers';
import { useEmailPanel } from '../contexts/EmailPanelContext';
import { EmailDraft } from '../../../services/draftService';

/**
 * Attachments for a DUPLICATED draft.
 *
 * A large attachment lives in Cloud Storage and the draft doc carries only a
 * `storagePath` ref. Storage objects are not reference-counted: a real send
 * deletes the object server-side and discarding a draft in the composer
 * deletes every ref it hydrated. If the copy shared the original's ref,
 * sending or discarding EITHER draft would delete the file out from under the
 * other (which then fails at send with "missing from storage"). So a ref is
 * never shared - it is reduced to metadata only, which the composer hydrates
 * as an error chip that blocks Send until the file is attached again. Inline
 * base64 items are independent copies and stay as they are.
 */
export function attachmentsForDuplicate(list: unknown): { attachments: any[]; droppedRefs: string[] } {
  const attachments: any[] = [];
  const droppedRefs: string[] = [];
  if (!Array.isArray(list)) return { attachments, droppedRefs };

  list.forEach((raw) => {
    if (!raw || typeof raw !== 'object') return;
    // `rest` is a fresh object, so the copy never aliases the source item.
    const { storagePath, ...rest } = raw as any;
    if (typeof storagePath === 'string' && storagePath && !rest.content) {
      const meta: any = {};
      if (typeof rest.filename === 'string' && rest.filename) meta.filename = rest.filename;
      if (typeof rest.contentType === 'string' && rest.contentType) meta.contentType = rest.contentType;
      if (typeof rest.size === 'number' && rest.size > 0) meta.size = rest.size;
      attachments.push(meta);
      droppedRefs.push(meta.filename || 'attachment');
      return;
    }
    attachments.push(rest);
  });

  return { attachments, droppedRefs };
}

interface DraftsPanelProps {
  // Optional props for customization
  maxHeight?: string;
  showSearch?: boolean;
  showFilters?: boolean;
}

const DraftsPanel: React.FC<DraftsPanelProps> = ({
  // 'none': the flex column already bounds the list; a pixel cap created a
  // second nested scroller on phones (stuttery chained touch scrolling) and
  // stranded dead space below the list on tall screens.
  maxHeight = 'none',
  showSearch = true,
  showFilters = true
}) => {
  const {
    userDrafts,
    isLoadingDrafts,
    draftError,
    deleteDraft,
    setEmailPanelView,
    setDraftMode,
    clearDraftError,
    saveDraft
  } = useEmailPanel();

  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'updated' | 'created' | 'subject'>('updated');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Filter and sort drafts
  const filteredAndSortedDrafts = useMemo(() => {
    let filtered = userDrafts;

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(draft =>
        draft.subject.toLowerCase().includes(searchLower) ||
        draft.textBody.toLowerCase().includes(searchLower) ||
        draft.to.some(recipient => recipient.toLowerCase().includes(searchLower))
      );
    }

    // Copy before sort — `filtered` aliases the context's userDrafts array
    // when no search filter is applied, so an in-place sort would mutate
    // shared state and break React change detection.
    filtered = [...filtered];

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortBy) {
        case 'subject':
          aValue = a.subject.toLowerCase();
          bValue = b.subject.toLowerCase();
          break;
        case 'created':
          aValue = a.createdAt;
          bValue = b.createdAt;
          break;
        case 'updated':
        default:
          aValue = a.updatedAt;
          bValue = b.updatedAt;
          break;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [userDrafts, searchTerm, sortBy, sortOrder]);

  const handleEditDraft = (draft: EmailDraft) => {
    setDraftMode('edit', draft);
    setEmailPanelView('editDraft');
  };

  const handleDeleteDraft = async (draftId: string) => {
    try {
      await deleteDraft(draftId);
      setShowDeleteConfirm(null);
      clearDraftError();
    } catch (error) {
      console.error('Failed to delete draft:', error);
    }
  };

  const handleDuplicateDraft = async (draft: EmailDraft) => {
    // Create a persisted copy immediately to avoid overriding the source on first save
    const { id: _ignored, ...draftWithoutId } = draft;
    // Never share a Storage ref between two drafts (see attachmentsForDuplicate).
    const copied = attachmentsForDuplicate(draftWithoutId.attachments);
    const duplicatedPayload = {
      subject: `${draft.subject} (Copy)`,
      htmlBody: draftWithoutId.htmlBody,
      to: draftWithoutId.to,
      cc: draftWithoutId.cc,
      bcc: draftWithoutId.bcc,
      attachments: copied.attachments,
      senderAccountId: draftWithoutId.senderAccountId,
      isReply: draftWithoutId.isReply,
      isForward: draftWithoutId.isForward,
      // A duplicated forward must not re-include originals the owner removed.
      // linkedInvoiceId is deliberately NOT copied (a copy must not mark the
      // invoice SENT a second time).
      ...(Array.isArray(draftWithoutId.forwardExcludeNames) ? { forwardExcludeNames: draftWithoutId.forwardExcludeNames } : {}),
      ...(draftWithoutId.forwardFromGraphId ? { forwardFromGraphId: draftWithoutId.forwardFromGraphId } : {}),
    } as Partial<EmailDraft>;

    try {
      const newId = await saveDraft(duplicatedPayload);
      if (copied.droppedRefs.length > 0) {
        // The composer's own chip wording is about drafts that were too large
        // to save, which would mislead here - say what actually happened.
        // (A toast outlives this panel, which unmounts on the view switch below.)
        const n = copied.droppedRefs.length;
        const names = copied.droppedRefs.slice(0, 2).join(', ') + (n > 2 ? ` +${n - 2} more` : '');
        toast.info(
          `Draft duplicated. ${names} ${n === 1 ? 'was' : 'were'} not copied - attach ${n === 1 ? 'it' : 'them'} again in the copy before sending.`,
        );
      }
      // Open the new copy in edit mode
      setDraftMode('edit', {
        id: newId,
        userId: '',
        subject: duplicatedPayload.subject || '',
        htmlBody: duplicatedPayload.htmlBody || '',
        textBody: '',
        to: duplicatedPayload.to || [],
        cc: duplicatedPayload.cc || [],
        bcc: duplicatedPayload.bcc || [],
        attachments: duplicatedPayload.attachments || [],
        senderAccountId: duplicatedPayload.senderAccountId || '',
        isReply: !!duplicatedPayload.isReply,
        isForward: !!duplicatedPayload.isForward,
        createdAt: undefined as any,
        updatedAt: undefined as any,
        status: 'draft'
      } as EmailDraft);

      setEmailPanelView('editDraft', {
        subject: duplicatedPayload.subject || '',
        html: duplicatedPayload.htmlBody || '',
        to: duplicatedPayload.to || [],
        cc: duplicatedPayload.cc || [],
        bcc: duplicatedPayload.bcc || [],
        attachments: duplicatedPayload.attachments as any,
        isReply: !!duplicatedPayload.isReply,
        isForward: !!duplicatedPayload.isForward,
      } as any);
    } catch (error) {
      console.error('Failed to duplicate draft:', error);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Compute word count at render time so it's always correct regardless of
  // the writer (composer sets `wordCount`; Steve / Graph sync don't).
  const computeWordCount = (draft: EmailDraft): number => {
    if (typeof draft.wordCount === 'number' && draft.wordCount > 0) return draft.wordCount;
    const text = draft.textBody || (draft.htmlBody || '').replace(/<[^>]+>/g, ' ');
    const trimmed = text.replace(/\s+/g, ' ').trim();
    if (!trimmed) return 0;
    return trimmed.split(' ').length;
  };

  const getDraftTypeLabel = (draft: EmailDraft) => {
    if (draft.isReply) return 'Reply';
    if (draft.isForward) return 'Forward';
    return 'Draft';
  };

  const getDraftTypeColor = (draft: EmailDraft) => {
    if (draft.isReply) return 'badge-light-primary';
    if (draft.isForward) return 'badge-light-success';
    return 'badge-light-secondary';
  };

  if (isLoadingDrafts) {
    return (
      <div className="d-flex flex-column h-100">
        <div className="d-flex align-items-center justify-content-center flex-grow-1">
          <div className="d-flex align-items-center">
            <div className="spinner-border spinner-border-sm text-primary me-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="text-muted fw-semibold">Loading drafts...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column h-100">
      {/* Header with Search and Filters */}
      <div className="p-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="mb-0 fw-bold">Email Drafts ({filteredAndSortedDrafts.length})</h6>
          <button
            type="button"
            className="btn btn-sm btn-light-primary"
            onClick={() => {
              setDraftMode('new');
              setEmailPanelView('composeEmail');
            }}
            title="Create new draft"
          >
            <KTSVG path='/media/icons/duotune/general/gen035.svg' className='svg-icon-3 me-1' />
            New Draft
          </button>
        </div>

        {showSearch && (
          <div className="d-flex align-items-center gap-3">
            <div style={{ flex: '1' }}>
              <div className="position-relative">
                <KTSVG
                  path='/media/icons/duotune/general/gen021.svg'
                  className='svg-icon-3 position-absolute top-50 translate-middle-y ms-3'
                />
                <input
                  type="text"
                  className="form-control form-control-sm ps-10"
                  placeholder="Search drafts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {showFilters && (
              <div style={{ flex: '0 0 auto' }}>
                <select
                  className="form-select form-select-sm"
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [newSortBy, newSortOrder] = e.target.value.split('-');
                    setSortBy(newSortBy as 'updated' | 'created' | 'subject');
                    setSortOrder(newSortOrder as 'asc' | 'desc');
                  }}
                >
                  <option value="updated-desc">Recently Updated</option>
                  <option value="updated-asc">Oldest Updated</option>
                  <option value="created-desc">Recently Created</option>
                  <option value="created-asc">Oldest Created</option>
                  <option value="subject-asc">Subject A-Z</option>
                  <option value="subject-desc">Subject Z-A</option>
                </select>
              </div>
            )}
          </div>
        )}

        {draftError && (
          <div className="alert alert-danger alert-dismissible fade show mt-3" role="alert">
            <span>{draftError}</span>
            <button
              type="button"
              className="btn-close"
              onClick={clearDraftError}
            ></button>
          </div>
        )}
      </div>

      {/* Drafts List */}
      <div className="flex-grow-1 overflow-auto" style={{ maxHeight }}>
        {filteredAndSortedDrafts.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center flex-grow-1 p-5">
            <div className="text-center">
              <i className="bi bi-file-earmark-text text-muted fs-2x mb-3"></i>
              <h6 className="text-muted fw-semibold mb-1">
                {searchTerm ? 'No drafts match your search' : 'No drafts found'}
              </h6>
              <p className="text-muted fs-7 mb-3">
                {searchTerm ? 'Try adjusting your search terms' : 'Start composing an email to create your first draft'}
              </p>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => {
                  setDraftMode('new');
                  setEmailPanelView('composeEmail');
                }}
              >
                <KTSVG path='/media/icons/duotune/general/gen035.svg' className='svg-icon-3 me-1' />
                Compose Email
              </button>
            </div>
          </div>
        ) : (
          <div className="p-3">
            {filteredAndSortedDrafts.map((draft) => (
              <div
                key={draft.id}
                // Hover lift lives in CSS under @media (hover: hover) — the JS
                // mouseenter version stuck permanently on touch (tap fires
                // mouseenter, mouseleave never comes).
                className="card mb-3 border border-gray-300 cursor-pointer draft-card"
                style={{
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.15s ease',
                }}
                onClick={() => handleEditDraft(draft)}
              >
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="flex-grow-1 min-w-0">
                      <div className="d-flex align-items-center mb-1">
                        <h6 className="text-gray-900 fw-bold mb-0 text-truncate me-2">
                          {draft.subject || 'No Subject'}
                        </h6>
                        <span className={`badge ${getDraftTypeColor(draft)} badge-sm`}>
                          {getDraftTypeLabel(draft)}
                        </span>
                      </div>
                      <div className="text-muted fs-7 mb-1 text-truncate">
                        To: {draft.to.join(', ')}
                        {draft.cc && draft.cc.length > 0 && `, CC: ${draft.cc.join(', ')}`}
                      </div>
                      <div className="text-muted fs-7">
                        {formatDate(draft.updatedAt)} • {computeWordCount(draft)} words
                      </div>
                    </div>

                    <div className="d-flex align-items-center">
                      {/* Quick Actions. (No Edit button — tapping the card
                          itself edits; the third button just crowded phones.) */}
                      <button
                        type="button"
                        className="btn btn-icon btn-sm btn-light-info me-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicateDraft(draft);
                        }}
                        title="Duplicate draft"
                      >
                        <KTSVG path='/media/icons/duotune/general/gen028.svg' className='svg-icon-4' />
                      </button>

                      <button
                        type="button"
                        className="btn btn-icon btn-sm btn-light-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteConfirm(draft.id ?? null);
                        }}
                        title="Delete draft"
                      >
                        <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-4' />
                      </button>
                    </div>
                  </div>

                  {/* Draft Preview */}
                  <div className="text-gray-600 fs-6">
                    {truncateText(draft.textBody || 'No content preview available', 150)}
                  </div>

                  {/* Attachments indicator */}
                  {draft.attachments && draft.attachments.length > 0 && (
                    <div className="mt-2">
                      <i className="bi bi-paperclip text-muted fs-7 me-1"></i>
                      <span className="text-muted fs-7">
                        {draft.attachments.length} attachment{draft.attachments.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal fade show" style={{ display: "block", zIndex: 1055 }} aria-modal="true" role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Draft</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteConfirm(null)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this draft? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                {/* Full-size buttons: this is the one dialog where a mis-tap
                    is destructive — don't shrink the targets. */}
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteConfirm(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => handleDeleteDraft(showDeleteConfirm)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showDeleteConfirm && <div className="modal-backdrop fade show" style={{zIndex: 1051}}></div>}
    </div>
  );
};

export default DraftsPanel;
